/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Soyun Lee (sylee2@rsupport.com)
 *
 * @description
 *
 * @created at Mon Mar 24 2025
 **/

import { useState } from "react";
import styled from "@emotion/styled";

import {
	Column,
	Icon,
	Row,
	Tab,
	TabContainer,
	TabContent,
	TabItem,
	Typography
} from "@/frameworks/designStuff/components";

import { usePopover } from "@/frameworks/core/hooks";
import { POPOVER_ID } from "@/frameworks/core/commons";
import { hoverScrollCss } from "@/frameworks/designStuff";
import { iconCategoryMap } from "@/frameworks/core/components/Chat/EmoticonPickers/SystemEmoticon";
import { useEmojiManager } from "@/frameworks/core/chat/useEmojiManager";

export const RECENT_EMOJIS = "emojiCel";

export default function TotalReactionPopup({ onSelect }: { onSelect: (reaction: string) => void }) {
	const { hide } = usePopover();
	const [activeCategory, setActiveCategory] = useState("recent");
	const { activeEmojis, saveRecentEmoji } = useEmojiManager(activeCategory);

	const handleRecentEmoji = (emoji: string) => {
		hide(POPOVER_ID.EMOJI_TOTAL_PICKER);
		saveRecentEmoji(emoji);
		onSelect(emoji);
	};

	return (
		<Wrapper>
			<TabContainer activeId={activeCategory} allActive={true}>
				<RepresentativeWrapper>
					<WrapperTab>
						{iconCategoryMap.map((category: { icon: DesignStuff.Icon.Name; category: string }) => (
							<TabItem
								key={category.category}
								id={category.category}
								onClick={() => setActiveCategory(category.category)}
							>
								<Icon name={category.icon} />
							</TabItem>
						))}
					</WrapperTab>
				</RepresentativeWrapper>
				<TabContent activeId={activeCategory}>
					<WrapContentEmotion gap={8} css={hoverScrollCss()}>
						{activeEmojis.map((category) => (
							<Column gap={8} key={category.slug} id={category.slug}>
								<Typography typoSize="Title2" style={{ marginTop: "16px" }}>
									{category.name}
								</Typography>
								<div>
									{category.emojis.map((emoji) => (
										<Emoji
											key={emoji.slug}
											className="chat-font"
											onClick={() => handleRecentEmoji(emoji.slug)}
										>
											{emoji.emoji}
										</Emoji>
									))}
								</div>
							</Column>
						))}
					</WrapContentEmotion>
				</TabContent>
			</TabContainer>
		</Wrapper>
	);
}

const RepresentativeWrapper = styled(Row)`
	align-items: center;
	width: 100%;
`;
const WrapperTab = styled(Tab)`
	height: 42px;
	padding: 0px 2px !important;
	width: 264px;
`;
const Wrapper = styled(Column)`
	width: 455px;
	height: 387px;
	background: ${({ theme }) => theme.colors.neutral_00_100};
	padding: 12px 0px 16px 20px;
	box-shadow:
		0px 8px 16px 0px rgba(0, 0, 0, 0.2),
		0px 0px 4px 0px rgba(0, 0, 0, 0.04);
	border-radius: 8px;
`;
const WrapContentEmotion = styled(Column)`
	overflow: auto;

	padding-right: 19px;
	height: 300px;
`;

const Emoji = styled.button`
	border-radius: 6px;
	padding: 4px;
	width: 40px;
	height: 40px;
	font-size: 29px;
	cursor: pointer;
	&:hover {
		background-color: ${({ theme }) => theme.colors.secondary_80_8};
	}
`;
